import type { ValidationIssue } from '../core/issues.js';
import type { Option } from './_shared.js';
import type { ActivityCheckContext } from './types.js';

import { optionText, optionValue } from './_shared.js';
import { activityIssue, asRecord } from './types.js';

/**
 * The checks more than one activity type needs, held once.
 *
 * The two things every list-of-options activity has to get right, checked here
 * because five types share them.
 *
 * An option with no visible text renders as a blank the player cannot tell apart from
 * its neighbours; an option routing to an outcome the writer never defined sends the
 * story somewhere it cannot go. Both are well-shaped JSON, so neither is a schema's
 * job — and both are exactly what a model producing an activity from a brief gets
 * wrong, by inventing a fourth outcome for a fourth option.
 */
export const optionIssues = (
  type: string,
  options: unknown[],
  ctx: ActivityCheckContext,
  { requireOutcomes = false }: { requireOutcomes?: boolean } = {},
): ValidationIssue[] => {
  const issue = activityIssue(type);
  const issues: ValidationIssue[] = [];

  for (const [index, raw] of options.entries()) {
    const option = asRecord(raw) as Option;
    const path = [...ctx.path, 'setup', 'options', index];

    if (!optionText(option)) {
      issues.push(issue('optionText', `Option ${index + 1} has no text to show the player.`, path, ctx, raw));
    }

    const outcomeId = (option.outcomeId ?? '').trim();
    if (!outcomeId) {
      if (requireOutcomes) {
        issues.push(issue(
          'optionOutcome',
          `Option ${index + 1} (${optionText(option) || 'untitled'}) names no outcome, so choosing it routes nowhere.`,
          [...path, 'outcomeId'],
          ctx,
          raw,
        ));
      }
      continue;
    }

    if (ctx.outcomeIds && !ctx.outcomeIds.includes(outcomeId)) {
      issues.push(issue(
        'unknownOutcome',
        `Option ${index + 1} routes to outcome '${outcomeId}', which is not one of the node's outcomes (${ctx.outcomeIds.join(', ') || 'none'}).`,
        [...path, 'outcomeId'],
        ctx,
        outcomeId,
      ));
    }
  }

  return issues;
};

/** Every option value in a list, for checking a correct answer names one of them. */
export const valuesOf = (options: unknown[]): string[] =>
  options.map(option => optionValue(asRecord(option) as Option)).filter(Boolean);

/** A non-empty options list is the floor for every type that plays as a choice. */
export const requireOptions = (
  type: string,
  options: unknown[],
  ctx: ActivityCheckContext,
  field = 'options',
): ValidationIssue[] =>
  options.length
    ? []
    : [activityIssue(type)('empty', `No ${field} are written — the player has nothing to choose from.`, [...ctx.path, 'setup', field], ctx)];

export const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * "The answer names an option that exists", which five types need and which is the single
 * most common way a produced activity is well-shaped and still broken: a model writes the
 * option's *label* where its `value` belongs, and every answer then grades wrong.
 *
 * `field` names the setup key so each type reports its own; `index` is set when the field
 * holds a list, so one bad entry in `correctAnswerMultiple` points at that entry.
 */
export const correctAnswerIssues = (
  { type, field, answers, values, ctx, indexed = false }: {
    type: string;
    field: string;
    answers: string[];
    values: string[];
    ctx: ActivityCheckContext;
    indexed?: boolean;
  },
): ValidationIssue[] => {
  const issue = activityIssue(type);
  const issues: ValidationIssue[] = [];

  for (const [index, answer] of answers.entries()) {
    if (!answer || values.includes(answer)) {
      continue;
    }
    const where = indexed ? `${field}[${index}]` : field;
    issues.push(issue(
      'unknownCorrectAnswer',
      `${where} '${answer}' matches no option value (${values.join(', ') || 'none'}).`,
      indexed ? [...ctx.path, 'setup', field, index] : [...ctx.path, 'setup', field],
      ctx,
      answer,
    ));
  }

  return issues;
};

/**
 * Where a key repeats one an earlier option already carries, as `{ index, first, key }`.
 * Options with no key are skipped — that is the missing-key complaint, not this one.
 *
 * Uniqueness is the same rule on every list, but the field it holds to is not: the
 * value-keyed types read `value`, `pick_one` reads `id`. The scan lives here so a fix
 * lands once; the field, the path and the message stay with the type that owns them, so
 * each still says which field a producer got wrong.
 */
export const repeatedKeys = (
  options: unknown[],
  keyOf: (option: Option) => string,
): { index: number; first: number; key: string }[] => {
  const firstSeen = new Map<string, number>();
  const repeats: { index: number; first: number; key: string }[] = [];

  for (const [index, raw] of options.entries()) {
    const key = keyOf(asRecord(raw) as Option);
    if (!key) {
      continue;
    }

    const first = firstSeen.get(key);
    if (first === undefined) {
      firstSeen.set(key, index);
      continue;
    }

    repeats.push({ index, first, key });
  }

  return repeats;
};

/**
 * Two options resolving to the same value. KUI's selection components key an option's
 * highlight state on its value, so clicking one lights up every option sharing it; and a
 * correct answer names an option by that value, so a duplicate leaves the grade unable to
 * say which option it meant.
 *
 * Only the types whose options are keyed by `value` ask for this. `pick_one` marks its
 * answer with an `id` and dialogue routes by `outcomeId`, so a `value` repeated on either
 * costs nothing — whatever the loose option shape lets an author write there. `pick_one`
 * runs `repeatedKeys` against `id` instead, in `pickOne.ts` beside its other id checks.
 *
 * A value is read the way `valuesOf` reads it, `id` fallback and all, so an option counts as
 * a duplicate on exactly the terms a correct answer would resolve it.
 */
export const duplicateValueIssues = (
  type: string,
  options: unknown[],
  ctx: ActivityCheckContext,
): ValidationIssue[] => {
  const issue = activityIssue(type);

  return repeatedKeys(options, optionValue).map(({ index, first, key }) => issue(
    'duplicateValue',
    `Option ${index + 1} repeats the value '${key}' that option ${first + 1} already uses, so selecting one highlights both.`,
    [...ctx.path, 'setup', 'options', index, 'value'],
    ctx,
    key,
  ));
};

/** A choice needs at least two options; one is a statement with a button. */
export const minimumOptions = (
  type: string,
  options: unknown[],
  ctx: ActivityCheckContext,
  minimum = 2,
): ValidationIssue[] =>
  options.length === 0 || options.length >= minimum
    ? []
    : [activityIssue(type)(
        'tooFewOptions',
        `Only ${options.length} option is written; a choice needs at least ${minimum}.`,
        [...ctx.path, 'setup', 'options'],
        ctx,
        options.length,
      )];

/**
 * An option with no value cannot be named by a correct answer or an order, so the
 * activity can never be graded right. `valuePath` places each option's `value`.
 */
export const missingValueIssues = (
  type: string,
  options: unknown[],
  ctx: ActivityCheckContext,
  {
    valuePath = (index: number) => [...ctx.path, 'setup', 'options', index, 'value'],
    label = 'Option',
  }: { valuePath?: (index: number) => (string | number)[]; label?: string } = {},
): ValidationIssue[] =>
  options.flatMap((raw, index) => optionValue(asRecord(raw) as Option)
    ? []
    : [activityIssue(type)('optionValue', `${label} ${index + 1} has no value, so no answer can name it.`, valuePath(index), ctx, raw)]);

const FEEDBACK_KEYS = ['correct', 'incorrect'] as const;

/**
 * Feedback is optional, but once written it is a pair: the player sees one half or the
 * other, so a missing half or a half with no title or message shows a blank response.
 */
export const feedbackIssues = (type: string, feedback: unknown, ctx: ActivityCheckContext): ValidationIssue[] => {
  if (feedback === undefined || feedback === null) {
    return [];
  }
  const issue = activityIssue(type);
  const pair = asRecord(feedback);
  const path = [...ctx.path, 'setup', 'feedback'];
  return FEEDBACK_KEYS.flatMap((key) => {
    const half = pair[key];
    if (half === undefined || half === null) {
      return [issue('feedback', `feedback.${key} is missing; written feedback needs both halves.`, [...path, key], ctx)];
    }
    const fields = asRecord(half);
    return (['title', 'message'] as const).flatMap(field => trimmed(fields[field])
      ? []
      : [issue('feedback', `feedback.${key}.${field} is empty.`, [...path, key, field], ctx, half)]);
  });
};

const wordCount = (text: string): number => text.split(/\s+/).filter(Boolean).length;

/** A warning when copy runs past what its surface can show at a glance. */
export const wordLimitIssues = (
  { type, text, limit, what, path, ctx }: {
    type: string;
    text: unknown;
    limit: number;
    what: string;
    path: (string | number)[];
    ctx: ActivityCheckContext;
  },
): ValidationIssue[] => {
  const words = wordCount(trimmed(text));
  return words <= limit
    ? []
    : [activityIssue(type)('tooLong', `${what} runs to ${words} words; keep it to ${limit}.`, path, ctx, words, 'warning')];
};
